import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, usePaginatedQuery, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";

export default function ConversasPage() {
    const {
        results: conversas = [],
        status: conversasStatus,
        loadMore: loadMoreConversas,
    } = usePaginatedQuery(
        api.index.conversations_listPaginated,
        {},
        { initialNumItems: 15 },
    );
    const clients = useQuery(api.index.clients_list) ?? [];
    const clientsById = useMemo(
        () => new Map(clients.map((c) => [c._id, c])),
        [clients],
    );
    const [selecionada, setSelecionada] = useState<string | null>(null);
    useEffect(() => {
        if (
            !selecionada &&
            Array.isArray(conversas) &&
            conversas.length > 0 &&
            conversas[0]?._id
        ) {
            setSelecionada(conversas[0]._id as string);
        }
    }, [conversas, selecionada]);
    const {
        results: msgsResults,
        status: msgsStatus,
        loadMore: loadMoreMsgs,
    } = usePaginatedQuery(
        api.index.conversations_listMessagesPaginated,
        selecionada ? { conversationId: selecionada as any } : "skip",
        { initialNumItems: 25 },
    );
    const enviar = useMutation(api.index.messages_sendFromBarber);
    const salvarCliente = useMutation(api.index.clients_upsert);
    const cliente = useMemo(() => {
        const conv = conversas.find(
            (c) => (c._id as unknown as string) === selecionada,
        );
        if (!conv) return null;
        return clientsById.get(conv.clientId as any) ?? null;
    }, [conversas, selecionada, clientsById]);
    const [texto, setTexto] = useState("");
    const messagesContainerRef = useRef<HTMLDivElement | null>(null);
    const conversationsContainerRef = useRef<HTMLDivElement | null>(null);
    const [showLoadOlderBtn, setShowLoadOlderBtn] = useState(false);
    const [showLoadMoreConversasBtn, setShowLoadMoreConversasBtn] =
        useState(false);

    const msgsAsc = useMemo(() => {
        const arr = Array.isArray(msgsResults) ? [...msgsResults] : [];
        arr.sort((a: any, b: any) => a.timestamp - b.timestamp);
        return arr;
    }, [msgsResults]);
    const messageCount = Array.isArray(msgsAsc) ? msgsAsc.length : 0;

    const loadingOlderRef = useRef(false);
    const prevScrollHeightRef = useRef<number | null>(null);

    useEffect(() => {
        const el = messagesContainerRef.current;
        if (!el) return;
        if (loadingOlderRef.current && prevScrollHeightRef.current != null) {
            const delta = el.scrollHeight - prevScrollHeightRef.current;
            el.scrollTop = el.scrollTop + delta;
            loadingOlderRef.current = false;
            prevScrollHeightRef.current = null;
            return;
        }
        el.scrollTop = el.scrollHeight;
    }, [selecionada, messageCount]);

    useEffect(() => {
        const el = messagesContainerRef.current;
        if (!el) return;
        const onScroll = () => {
            setShowLoadOlderBtn(el.scrollTop <= 10);
        };
        el.addEventListener("scroll", onScroll);
        onScroll();
        return () => el.removeEventListener("scroll", onScroll);
    }, [selecionada]);

    useEffect(() => {
        const el = conversationsContainerRef.current;
        if (!el) return;
        const onScroll = () => {
            const nearBottom =
                el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
            setShowLoadMoreConversasBtn(nearBottom);
        };
        el.addEventListener("scroll", onScroll);
        onScroll();
        return () => el.removeEventListener("scroll", onScroll);
    }, [conversas.length]);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                {/* Sidebar with conversations */}
                <div className="lg:col-span-4">
                    <div className="bg-white dark:bg-gray-800 lg:rounded-l-lg rounded-none border border-gray-200 dark:border-gray-700 border-r-0 overflow-hidden">
                        <div className="px-4 h-14 border-b border-gray-200 dark:border-gray-700 flex items-center">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                Conversas ativas
                            </h3>
                        </div>

                        <div
                            ref={conversationsContainerRef}
                            className="max-h-155 overflow-y-auto"
                        >
                            {(conversas ?? []).length === 0 ? (
                                <div className="p-6 text-center text-sm text-gray-600 dark:text-gray-300">
                                    Nenhuma conversa no momento
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {(
                                        (conversas ?? []) as Array<{
                                            _id: string;
                                            clientId: string;
                                            lastMessageTime: number;
                                        }>
                                    ).map((c) => {
                                        const cte = clientsById.get(
                                            c.clientId as any,
                                        );
                                        return (
                                            <button
                                                key={c._id}
                                                onClick={() =>
                                                    setSelecionada(
                                                        c._id as string,
                                                    )
                                                }
                                                className={`w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm ${
                                                    (c._id as unknown as string) ===
                                                    selecionada
                                                        ? "bg-emerald-50 dark:bg-emerald-900/20 border-r-2 border-emerald-500"
                                                        : ""
                                                }`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <div className="w-7 h-7 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                                                        <svg
                                                            className="w-4 h-4 text-gray-600 dark:text-gray-300"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium text-gray-900 dark:text-white truncate text-base">
                                                            {cte?.name ??
                                                                "Cliente"}
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                            {formatRelative(
                                                                c.lastMessageTime,
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                    {conversasStatus === "CanLoadMore" && (
                                        <div className="p-3">
                                            <button
                                                onClick={() =>
                                                    loadMoreConversas(20)
                                                }
                                                className={`w-full px-3 py-2 text-sm rounded-md border bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 border-gray-300 dark:border-gray-600 ${
                                                    showLoadMoreConversasBtn
                                                        ? "opacity-100"
                                                        : "opacity-0"
                                                } transition-opacity`}
                                            >
                                                Carregar mais contatos
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Main chat area */}
                <div className="lg:col-span-8">
                    {selecionada ? (
                        <div className="bg-white dark:bg-gray-800 lg:rounded-r-lg rounded-none border border-gray-200 dark:border-gray-700 border-l overflow-hidden flex flex-col h-155">
                            {cliente && (
                                <div className="px-4 h-14 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
                                    <div className="font-semibold text-gray-900 dark:text-white flex-1 text-base">
                                        {cliente.name}
                                        {cliente.phone && (
                                            <div className="text-xs font-normal text-gray-600 dark:text-gray-300">
                                                {cliente.phone}
                                            </div>
                                        )}
                                    </div>
                                    <label className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={!cliente.botEnabled}
                                            onChange={async (e) => {
                                                await salvarCliente({
                                                    _id: cliente._id,
                                                    name: cliente.name,
                                                    phone: cliente.phone,
                                                    email: cliente.email,
                                                    notes: cliente.notes,
                                                    botEnabled:
                                                        !e.target.checked,
                                                });
                                            }}
                                            className="w-4 h-4 text-emerald-600 bg-gray-100 border-gray-300 rounded focus:ring-emerald-500 focus:ring-2"
                                        />
                                        <span className="text-gray-700 dark:text-gray-300">
                                            Assumir conversa
                                        </span>
                                    </label>
                                </div>
                            )}

                            <div
                                ref={messagesContainerRef}
                                className="flex-1 p-3 space-y-1.5 overflow-auto bg-gray-50 dark:bg-gray-900"
                            >
                                {msgsStatus === "CanLoadMore" && (
                                    <div className="sticky top-0 z-10 flex justify-center pb-2">
                                        <button
                                            onClick={() => {
                                                const el =
                                                    messagesContainerRef.current;
                                                if (el) {
                                                    loadingOlderRef.current = true;
                                                    prevScrollHeightRef.current =
                                                        el.scrollHeight;
                                                }
                                                loadMoreMsgs(30);
                                            }}
                                            disabled={
                                                msgsStatus !== "CanLoadMore"
                                            }
                                            className={`px-3 py-1 text-xs rounded-md border ${
                                                showLoadOlderBtn
                                                    ? "opacity-100"
                                                    : "opacity-0"
                                            } transition-opacity bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700`}
                                        >
                                            Carregar mais mensagens
                                        </button>
                                    </div>
                                )}
                                {(
                                    (msgsAsc ?? []) as Array<{
                                        _id: string;
                                        authorType:
                                            | "client"
                                            | "assistant"
                                            | "barber"
                                            | "tool";
                                        body: string;
                                        timestamp: number;
                                    }>
                                ).map((m) => (
                                    <div
                                        key={m._id}
                                        className={`flex ${
                                            m.authorType === "barber" ||
                                            m.authorType === "assistant" ||
                                            m.authorType === "tool"
                                                ? "justify-end"
                                                : "justify-start"
                                        }`}
                                    >
                                        {m.authorType === "tool" ? (
                                            <div className="relative px-2 py-1.5 text-xs leading-relaxed max-w-[82%] break-words bg-yellow-50 text-yellow-900 border border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-200 dark:border-yellow-800 rounded-md">
                                                <div className="mb-0.5">
                                                    <div className="flex items-center gap-1">
                                                        <svg
                                                            className="w-3.5 h-3.5"
                                                            viewBox="0 0 24 24"
                                                            fill="currentColor"
                                                        >
                                                            <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 0 0-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 0 0-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 0 0-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 0 0-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 0 0 1.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.607 2.285.098 2.573-1.065z" />
                                                        </svg>
                                                        <span className="font-medium">
                                                            Tool
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="pr-8 whitespace-pre-wrap">
                                                    {m.body}
                                                </div>
                                                <span className="absolute bottom-1 right-2 text-[10px] opacity-70 select-none">
                                                    {formatTime(m.timestamp)}
                                                </span>
                                            </div>
                                        ) : (
                                            <div
                                                className={`relative px-2 py-1.5 text-sm leading-relaxed max-w-[82%] break-words ${
                                                    m.authorType === "client"
                                                        ? "bg-white text-[#111b21] border border-gray-200 dark:bg-[#1f2c34] dark:text-gray-100 dark:border-transparent rounded-md rounded-tl-sm"
                                                        : m.authorType ===
                                                            "barber"
                                                          ? "bg-indigo-100 text-[#111b21] dark:bg-indigo-700 dark:text-white rounded-md rounded-tr-sm"
                                                          : "bg-[#d9fdd3] text-[#111b21] dark:bg-[#005c4b] dark:text-white rounded-md rounded-tr-sm"
                                                }`}
                                            >
                                                <div className="pr-8 whitespace-pre-wrap">
                                                    {m.body}
                                                </div>
                                                <span className="absolute bottom-1 right-2 text-[10px] opacity-70 select-none">
                                                    {formatTime(m.timestamp)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {cliente && selecionada && (
                                <form
                                    onSubmit={async (e) => {
                                        e.preventDefault();
                                        if (!texto.trim()) return;
                                        await enviar({
                                            conversationId: selecionada as any,
                                            clientId: cliente._id,
                                            body: texto.trim(),
                                        });
                                        setTexto("");
                                    }}
                                    className="p-3 border-t border-gray-200 dark:border-gray-700 flex gap-2"
                                >
                                    <input
                                        value={texto}
                                        onChange={(e) =>
                                            setTexto(e.target.value)
                                        }
                                        placeholder="Escreva uma mensagem..."
                                        className="flex-1 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm"
                                    />
                                    <button className="px-4 py-2 bg-emerald-500 text-white rounded-md font-medium hover:bg-emerald-600 transition-colors text-sm">
                                        Enviar
                                    </button>
                                </form>
                            )}
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-gray-800 rounded-none border border-gray-200 dark:border-gray-700 p-4 text-sm text-gray-700 dark:text-gray-300">
                            Selecione uma conversa na lista para visualizar as
                            mensagens
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function formatRelative(ms: number) {
    // Ensure we have a valid timestamp
    if (!ms || ms <= 0) return "Nunca";

    const now = Date.now();
    const diff = Math.max(0, now - ms);
    const s = Math.floor(diff / 1000);

    if (s < 30) return "Agora";
    if (s < 60) return `${s}s`;

    const m = Math.floor(diff / 60000);
    if (m < 60) return `${m}min`;

    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;

    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d`;

    // For older dates, show actual date
    return new Date(ms).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
    });
}

function formatTime(ms: number) {
    if (!ms || ms <= 0) return "";
    const d = new Date(ms);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
}
